/**
 * Cloudflare Client for Reno Quoting Pro
 * Replaces Supabase client with Cloudflare Workers API
 */

import { env, getApiBaseUrl, getR2PublicUrl, logError } from "@/lib/env";

interface CloudflareConfig {
  apiUrl: string;
  r2PublicUrl?: string;
}

interface QueryResult<T> {
  data: T | null;
  error: Error | null;
}

interface MutationResult<T> {
  data: T | null;
  error: Error | null;
}

class CloudflareClient {
  private apiUrl: string;
  private r2PublicUrl: string;

  // Security allowlists to prevent SQL injection
  private static readonly ALLOWED_TABLES = new Set([
    "projects",
    "project_items",
    "project_services",
    "products",
    "services",
    "quotes",
    "quote_items",
    "quote_services",
    "customers",
  ]);

  private static readonly ALLOWED_COLUMNS = new Set([
    "id",
    "name",
    "email",
    "phone",
    "address",
    "budget",
    "timeframe",
    "status",
    "project_id",
    "product_id",
    "service_id",
    "quantity",
    "price",
    "description",
    "created_at",
    "updated_at",
    "category",
    "title",
    "content",
    "quote_id",
  ]);

  private static readonly ALLOWED_OPERATORS = new Set([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "like",
  ]);

  constructor(config: CloudflareConfig) {
    this.apiUrl = config.apiUrl || getApiBaseUrl();
    this.r2PublicUrl = config.r2PublicUrl || getR2PublicUrl();
  }

  validateTableName(table: string): void {
    if (!CloudflareClient.ALLOWED_TABLES.has(table)) {
      throw new Error(`Invalid table name: ${table}`);
    }
  }

  validateColumnName(column: string): void {
    if (!CloudflareClient.ALLOWED_COLUMNS.has(column)) {
      throw new Error(`Invalid column name: ${column}`);
    }
  }

  validateOperator(operator: string): void {
    if (!CloudflareClient.ALLOWED_OPERATORS.has(operator)) {
      throw new Error(`Invalid operator: ${operator}`);
    }
  }

  sanitizeValue(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    // Convert to string and remove potentially dangerous characters
    return String(value).replace(/['"\\;]/g, "");
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<QueryResult<T>> {
    try {
      const response = await fetch(`${this.apiUrl}${endpoint}`, {
        ...options,
        credentials: "include", // CRITICAL: Include cookies for authentication
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      return { data: result.data || result, error: null };
    } catch (error) {
      logError(`API request failed: ${endpoint}`, error as Error);
      return { data: null, error: error as Error };
    }
  }

  // Database query builder pattern (mimicking Supabase)
  from(table: string) {
    this.validateTableName(table);
    const builder = new QueryBuilder(this, table);
    return builder;
  }

  // Storage operations
  storage = {
    from: (bucket: string) => ({
      upload: async (path: string, file: File) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("path", path);

        const response = await fetch(`${this.apiUrl}/api/v1/storage/${bucket}/upload`, {
          method: "POST",
          credentials: "include", // Include cookies for authentication
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          return { data: null, error: new Error(error.error) };
        }

        const result = await response.json();
        return { data: result.data, error: null };
      },

      getPublicUrl: (path: string) => {
        return {
          data: {
            publicUrl: `${this.r2PublicUrl}/${bucket}/${path}`,
          },
        };
      },

      remove: async (paths: string[]) => {
        const response = await fetch(`${this.apiUrl}/api/v1/storage/${bucket}/delete`, {
          method: "DELETE",
          credentials: "include", // Include cookies for authentication
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paths }),
        });

        if (!response.ok) {
          const error = await response.json();
          return { data: null, error: new Error(error.error) };
        }

        return { data: { message: "Files deleted" }, error: null };
      },
    }),
  };

  // Direct API access for custom endpoints
  async api<T>(endpoint: string, options?: RequestInit): Promise<QueryResult<T>> {
    return this.request<T>(`/api/v1${endpoint}`, options);
  }
}

// Query builder class to mimic Supabase's chainable API
class QueryBuilder<T = any> {
  private client: CloudflareClient;
  private table: string;
  private selectColumns = "*";
  private filters: Array<{ column: string; operator: string; value: any }> = [];
  private orderByColumn?: string;
  private orderDirection: "asc" | "desc" = "asc";
  private limitCount?: number;

  constructor(client: CloudflareClient, table: string) {
    this.client = client;
    this.table = table;
  }

  select(columns = "*"): this {
    this.selectColumns = columns;
    return this;
  }

  eq(column: string, value: any): this {
    this.client.validateColumnName(column);
    this.filters.push({ column, operator: "eq", value: this.client.sanitizeValue(value) });
    return this;
  }

  neq(column: string, value: any): this {
    this.client.validateColumnName(column);
    this.filters.push({ column, operator: "neq", value: this.client.sanitizeValue(value) });
    return this;
  }

  gt(column: string, value: any): this {
    this.client.validateColumnName(column);
    this.filters.push({ column, operator: "gt", value: this.client.sanitizeValue(value) });
    return this;
  }

  gte(column: string, value: any): this {
    this.client.validateColumnName(column);
    this.filters.push({ column, operator: "gte", value: this.client.sanitizeValue(value) });
    return this;
  }

  lt(column: string, value: any): this {
    this.client.validateColumnName(column);
    this.filters.push({ column, operator: "lt", value: this.client.sanitizeValue(value) });
    return this;
  }

  lte(column: string, value: any): this {
    this.client.validateColumnName(column);
    this.filters.push({ column, operator: "lte", value: this.client.sanitizeValue(value) });
    return this;
  }

  like(column: string, value: string): this {
    this.client.validateColumnName(column);
    this.filters.push({ column, operator: "like", value: this.client.sanitizeValue(value) });
    return this;
  }

  order(column: string, options?: { ascending?: boolean }): this {
    this.client.validateColumnName(column);
    this.orderByColumn = column;
    this.orderDirection = options?.ascending === false ? "desc" : "asc";
    return this;
  }

  limit(count: number): this {
    this.limitCount = count;
    return this;
  }

  async single(): Promise<QueryResult<T>> {
    this.limitCount = 1;
    const result = await this.execute();
    if (result.data && Array.isArray(result.data) && result.data.length > 0) {
      return { data: result.data[0], error: null };
    }
    return { data: null, error: result.error };
  }

  async first(): Promise<T | null> {
    const result = await this.single();
    return result.data;
  }

  async insert(data: Partial<T> | Partial<T>[]): Promise<MutationResult<T>> {
    const endpoint = `/${this.table}`;
    const response = await this.client.api<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response;
  }

  async update(data: Partial<T>): Promise<MutationResult<T>> {
    if (this.filters.length === 0) {
      return { data: null, error: new Error("Update requires at least one filter") };
    }

    const params = new URLSearchParams();
    this.filters.forEach((f) => {
      // Validate filter parameters
      this.client.validateColumnName(f.column);
      this.client.validateOperator(f.operator);
      params.append(`${f.column}.${f.operator}`, f.value);
    });

    const endpoint = `/${this.table}?${params.toString()}`;
    const response = await this.client.api<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    return response;
  }

  async delete(): Promise<MutationResult<{ success: boolean }>> {
    if (this.filters.length === 0) {
      return { data: null, error: new Error("Delete requires at least one filter") };
    }

    // Special handling for single ID-based deletes
    if (
      this.filters.length === 1 &&
      this.filters[0].column === "id" &&
      this.filters[0].operator === "eq"
    ) {
      const id = this.filters[0].value;
      const endpoint = `/${this.table}/${id}`;
      const response = await this.client.api<{ success: boolean }>(endpoint, {
        method: "DELETE",
      });
      return response;
    }

    // For other filters, use query parameters (not typically supported by our API)
    const params = new URLSearchParams();
    this.filters.forEach((f) => {
      // Validate filter parameters
      this.client.validateColumnName(f.column);
      this.client.validateOperator(f.operator);
      params.append(`${f.column}.${f.operator}`, f.value);
    });

    const endpoint = `/${this.table}?${params.toString()}`;
    const response = await this.client.api<{ success: boolean }>(endpoint, {
      method: "DELETE",
    });
    return response;
  }

  private async execute(): Promise<QueryResult<T[]>> {
    const params = new URLSearchParams();

    if (this.selectColumns !== "*") {
      params.append("select", this.selectColumns);
    }

    this.filters.forEach((filter) => {
      // Validation already done in filter methods, but double-check
      this.client.validateColumnName(filter.column);
      this.client.validateOperator(filter.operator);
      params.append(`${filter.column}.${filter.operator}`, filter.value);
    });

    if (this.orderByColumn) {
      // Validation already done in order method, but double-check
      this.client.validateColumnName(this.orderByColumn);
      params.append("order", `${this.orderByColumn}.${this.orderDirection}`);
    }

    if (this.limitCount) {
      params.append("limit", this.limitCount.toString());
    }

    const endpoint = `/${this.table}?${params.toString()}`;
    return this.client.api<T[]>(endpoint);
  }

  // Execute the query and return results
  async then<TResult1 = QueryResult<T[]>, TResult2 = never>(
    onfulfilled?:
      | ((value: QueryResult<T[]>) => TResult1 | PromiseLike<TResult1>)
      | undefined
      | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

// Create singleton instance with validated environment variables
const cloudflare = new CloudflareClient({
  apiUrl: getApiBaseUrl(),
  r2PublicUrl: getR2PublicUrl(),
});

// Export for compatibility with existing code
export { cloudflare, CloudflareClient };

// Default export to match Supabase pattern
export default cloudflare;
